import { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { dialogs, utils, assets, serviceClient } from '@yola/ws-sdk';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import stockGallery from 'src/js/modules/stock-gallery';
import preloadImage from 'src/js/modules/utils/preload-image';

const { IMAGE_LOADING_DIALOG, PHOTO_SIZE_ERROR, PHOTO_LOAD_ERROR, FILE_TYPE_ERROR_DIALOG } =
  dialogTypes;
const {
  constants: { stockIds },
  helpers: { generateIstockImageSrc, generateAlternativeIstockBgImageSrc },
} = stockGallery;

function UploadImageContainer({
  children,
  onImageUploadStart = utils.noop,
  onImageUploadError = utils.noop,
  onImageUploadCancel = utils.noop,
  onImageUploadEnd = utils.noop,
  preserveSharedData = false,
  imagePlacement = assets.constants.IMAGE_PLACEMENTS.INLINE,
  onUrlPhotoLoadErrorDismiss = undefined,
  ...otherProps
}) {
  const cancelRef = useRef(false);

  const handleImageUpload = useCallback(
    async (fileList) => {
      const [imageFile] = fileList;
      if (!imageFile) return;
      onImageUploadStart();

      // Check whether selected file satisfies asset file size requirements
      const [, filesSizeError] = await utils.handleAsyncError(
        assets.operations.clientUploadAsset(imageFile)
      );

      if (filesSizeError) {
        console.error(filesSizeError);
        onImageUploadError(filesSizeError);
        return;
      }

      // Upload file and create media gallery asset
      dialogs.operations.show(IMAGE_LOADING_DIALOG, {
        onCancel: () => {
          dialogs.operations.hide({ preserveSharedData });
          cancelRef.current = true;
          onImageUploadCancel();
        },
      });

      const isOgTagPlacement = imagePlacement === assets.constants.IMAGE_PLACEMENTS.OG_TAG;

      const [response, createAssetError] = await utils.handleAsyncError(
        assets.operations.createMediaGalleryAssets({
          asset: imageFile,
          name: imageFile.name,
          ...(!isOgTagPlacement && { optimize: true }),
          image_placement: imagePlacement,
        })
      );

      if (createAssetError) {
        dialogs.operations.hide({ preserveSharedData });

        if (
          createAssetError.status === 413 &&
          createAssetError.response.data?.code === 'image_too_large'
        ) {
          const {
            data: { maxHeight, maxWidth },
          } = createAssetError.response;

          const { width, height } = await assets.helpers.getImageSize(
            URL.createObjectURL(imageFile)
          );
          dialogs.operations.show(PHOTO_SIZE_ERROR, {
            preserveSharedData,
            captions: {
              width,
              height,
              maxWidth,
              maxHeight,
            },
          });
        }

        console.error(createAssetError);
        onImageUploadError(createAssetError);
        return;
      }

      const {
        data: { source, name, altSource },
      } = response;

      if (!cancelRef.current) {
        onImageUploadEnd({ source, altSource, name, file: imageFile, isStockPhoto: false });
      }
    },
    [
      imagePlacement,
      preserveSharedData,
      onImageUploadCancel,
      onImageUploadEnd,
      onImageUploadStart,
      onImageUploadError,
    ]
  );

  const handleUnsplashPhoto = useCallback(
    async ({ imageId }) => {
      onImageUploadStart();

      const stockPhotoPromise = serviceClient.get().unsplashCreateAssetsFromPhoto({
        photoId: imageId,
        imagePlacement,
      });

      const [response, error] = await utils.handleAsyncError(stockPhotoPromise);

      if (error || !response) {
        dialogs.operations.show(PHOTO_LOAD_ERROR, {
          preserveSharedData,
          isApiRateExceeded: Boolean(error && error.status === 429),
        });

        console.error(error);
        onImageUploadError(error);

        return;
      }

      const {
        data: { source, altSource, name },
      } = response;

      onImageUploadEnd({ source, altSource, name, isStockPhoto: true });
    },
    [imagePlacement, onImageUploadEnd, onImageUploadError, onImageUploadStart, preserveSharedData]
  );

  const handleIstockPhoto = useCallback(
    async ({ imageId }) => {
      onImageUploadStart();

      const source = generateIstockImageSrc(imageId);
      const fullUrl = assets.helpers.addBaseHref(source);

      const [, error] = await utils.handleAsyncError(preloadImage(fullUrl));

      if (error) {
        dialogs.operations.show(PHOTO_LOAD_ERROR, {
          preserveSharedData,
        });

        console.error(error);
        onImageUploadError(error);

        return;
      }

      const isBackground = imagePlacement === assets.constants.IMAGE_PLACEMENTS.BACKGROUND;

      onImageUploadEnd({
        source,
        isStockPhoto: true,
        ...(isBackground && { altSource: generateAlternativeIstockBgImageSrc(imageId) }),
      });
    },
    [imagePlacement, onImageUploadEnd, onImageUploadError, onImageUploadStart, preserveSharedData]
  );

  const handleStockPhoto = useCallback(
    async ({ stockId, selectedPhotos }) => {
      const [selectedPhoto] = selectedPhotos;
      const { id } = selectedPhoto;

      dialogs.operations.show(IMAGE_LOADING_DIALOG, {
        onCancel: () => dialogs.operations.hide({ preserveSharedData }),
      });

      if (stockId === stockIds.UNSPLASH) {
        handleUnsplashPhoto({ imageId: id });
      }

      if (stockId === stockIds.ISTOCK) {
        handleIstockPhoto({ imageId: id });
      }
    },
    [preserveSharedData, handleUnsplashPhoto, handleIstockPhoto]
  );

  const handleUrlPhotoUpload = useCallback(
    async (url) => {
      if (!url) {
        return;
      }

      const fullUrl = assets.helpers.addBaseHref(url);

      const [, preloadError] = await utils.handleAsyncError(preloadImage(fullUrl || url));

      if (preloadError) {
        dialogs.operations.show(PHOTO_LOAD_ERROR, {
          preserveSharedData,
          isInvalidUrl: true,
          ...(onUrlPhotoLoadErrorDismiss && { onDismiss: onUrlPhotoLoadErrorDismiss }),
        });
        onImageUploadError(preloadError);
        return;
      }

      onImageUploadEnd({ source: url, isStockPhoto: false, isUrlPhoto: true });
    },
    [onImageUploadEnd, onImageUploadError, onUrlPhotoLoadErrorDismiss, preserveSharedData]
  );

  return children({
    onWrongFileType: () => dialogs.operations.show(FILE_TYPE_ERROR_DIALOG),
    onImageUpload: handleImageUpload,
    onStockPhotoUpload: handleStockPhoto,
    onUrlPhotoUpload: handleUrlPhotoUpload,
    preserveSharedData,
    ...otherProps,
  });
}

UploadImageContainer.propTypes = {
  children: PropTypes.func.isRequired,
  onImageUploadStart: PropTypes.func,
  onImageUploadEnd: PropTypes.func,
  onImageUploadError: PropTypes.func,
  onImageUploadCancel: PropTypes.func,
  preserveSharedData: PropTypes.bool,
  imagePlacement: PropTypes.string,
  onUrlPhotoLoadErrorDismiss: PropTypes.func,
};

export default UploadImageContainer;
